import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FaStickyNote, FaTimes } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchReadyToUploadCases,
  removeReadyToUploadCases,
  upsertReadyToUploadCase,
} from "../../store/allocation/readyCasesSlice";
import {
  updateAllocatedCases,
  resetAllocationState,
} from "../../store/allocation/allocateSlice";
import { useToast } from "../../components/utilities/Toast";
import CaseNotesModal from "../../components/allocation-dashboard/CaseNotesModal";
import { IST_TIMEZONE } from "../../utils/dateTime";
import { getSocket } from "../../socket/index";
import { readyToUploadCases } from "../../types/ReadyCasesTypes";
import {
  caseHrefFromAllocationId,
  liveCaseIdFromAllocationCaseId,
} from "../../utils/caseIds";
import { RedesignScopeTag } from "../../components/admin/unitsTableShared";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminMetaRow,
  AdminTableCard,
  AdminTableState,
  AdminSoftInput,
  AdminSoftSelect,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;

const toIstDateKey = (value: string | Date) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

const shiftDateKey = (dateKey: string, days: number) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
};

const formatDateButtonLabel = (dateKey: string) => {
  const [, month, day] = dateKey.split("-");
  return `${day} ${MONTHS[Number(month) - 1]}`;
};

const ALL_LABS = "ALL";

const INTERNAL_REDESIGN_CATEGORIES = [
  "Query",
  "Design Preference",
  "Note Missed",
  "Design Mistake",
  "File Mistake",
  "Client Preference Missed",
  "Minor Design Mistake",
  "Need to Improve Design Quality",
  "Other",
];

const INTERNAL_REDESIGN_SEVERITIES = ["Minor", "Major"] as const;

const labTokenFromCaseId = (caseId: string) =>
  liveCaseIdFromAllocationCaseId(caseId).slice(0, 2).toUpperCase();

const playNewCaseSound = () => {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const playTone = (freq: number, start: number, duration: number) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(start);
      oscillator.stop(start + duration);
    };

    const now = ctx.currentTime;
    playTone(880, now, 0.15);
    playTone(1174.66, now + 0.16, 0.2);
    window.setTimeout(() => void ctx.close(), 500);
  } catch {
    // ignore audio failures
  }
};

const ReadyToUpload = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);

  const canWriteReadyToUpload =
    user?.role === "Super Admin" ||
    employee?.role === "Admin" ||
    employee?.role === "Executive" ||
    employee?.role === "Super Admin" ||
    employee?.role === "CRM";
  const { readyToUpload, loading } = useAppSelector(
    (state) => state.readycases,
  );
  const allocationState = useAppSelector((state) => state.caseAllocation);
  const [selectedActions, setSelectedActions] = useState<
    Record<number, string>
  >({});

  const [searchTerm, setSearchTerm] = useState("");
  const [passkey, setPasskey] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [internalRedesignCategories, setInternalRedesignCategories] = useState<
    string[]
  >([]);
  const [internalRedesignSeverity, setInternalRedesignSeverity] = useState<
    "" | "Minor" | "Major"
  >("");
  const [internalAssignmentMode, setInternalAssignmentMode] = useState<
    "" | "old" | "new"
  >("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    allocationId: number;
    action: string;
  } | null>(null);

  const [open, setOpen] = useState(false);
  const [selectedAllocationId, setSelectedAllocationId] = useState<
    number | null
  >(null);
  const [selectedDateKey, setSelectedDateKey] = useState(() =>
    shiftDateKey(toIstDateKey(new Date()), -1),
  );
  const [selectedLab, setSelectedLab] = useState(ALL_LABS);
  const [blinkingCaseIds, setBlinkingCaseIds] = useState<Set<string>>(
    () => new Set(),
  );
  const initialLoadDoneRef = useRef(false);
  const knownAllocationIdsRef = useRef<Set<number>>(new Set());

  const shouldNotifyReadyToUpload =
    user?.role === "Operations" || employee?.role === "CRM";

  const dateFilters = useMemo(() => {
    const todayKey = toIstDateKey(new Date());
    const counts = readyToUpload.reduce<Record<string, number>>((acc, item) => {
      if (!item.updated_at) return acc;
      const key = toIstDateKey(item.updated_at);
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});

    return [0, 1, 2, 3].map((offset) => {
      const dateKey = shiftDateKey(todayKey, -offset);
      return {
        dateKey,
        dateLabel: formatDateButtonLabel(dateKey),
        hasCases: (counts[dateKey] ?? 0) > 0,
      };
    });
  }, [readyToUpload]);

  const casesOnSelectedDate = useMemo(
    () =>
      readyToUpload.filter((item) => {
        if (!item.updated_at) return false;
        return toIstDateKey(item.updated_at) === selectedDateKey;
      }),
    [readyToUpload, selectedDateKey],
  );

  const labFilters = useMemo(() => {
    const counts = casesOnSelectedDate.reduce<Record<string, number>>(
      (acc, item) => {
        const token = labTokenFromCaseId(item.case_id);
        if (!token) return acc;
        acc[token] = (acc[token] ?? 0) + 1;
        return acc;
      },
      {},
    );

    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([token, count]) => ({ token, count }));
  }, [casesOnSelectedDate]);

  useEffect(() => {
    if (selectedLab === ALL_LABS) return;
    if (!labFilters.some((lab) => lab.token === selectedLab)) {
      setSelectedLab(ALL_LABS);
    }
  }, [labFilters, selectedLab]);

  const filteredCases = casesOnSelectedDate.filter((item) => {
    const matchesSearch = item.case_id
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (selectedLab === ALL_LABS) return true;
    return labTokenFromCaseId(item.case_id) === selectedLab;
  });

  // Fetch cases
  useEffect(() => {
    dispatch(fetchReadyToUploadCases());
  }, [dispatch]);

  useEffect(() => {
    if (loading) return;
    for (const item of readyToUpload) {
      knownAllocationIdsRef.current.add(item.allocation_id);
    }
    initialLoadDoneRef.current = true;
  }, [loading, readyToUpload]);

  useEffect(() => {
    const socket = getSocket();
    socket.emit("join_ready_to_upload_room");

    const handleReadyCase = (row: readyToUploadCases) => {
      const isNew = !knownAllocationIdsRef.current.has(row.allocation_id);
      knownAllocationIdsRef.current.add(row.allocation_id);
      dispatch(upsertReadyToUploadCase(row));

      if (
        shouldNotifyReadyToUpload &&
        initialLoadDoneRef.current &&
        isNew
      ) {
        playNewCaseSound();
        showToast(`Ready to upload: ${row.case_id}`, "info", 2000);
        setBlinkingCaseIds((prev) => new Set(prev).add(row.case_id));
        window.setTimeout(() => {
          setBlinkingCaseIds((prev) => {
            const next = new Set(prev);
            next.delete(row.case_id);
            return next;
          });
        }, 2000);
      }
    };

    socket.on("ready_to_upload_case", handleReadyCase);
    const handleReadyCaseRemoved = (payload: {
      allocation_ids?: number[];
    }) => {
      const ids = (payload?.allocation_ids ?? []).map(Number).filter(Boolean);
      if (!ids.length) return;
      dispatch(removeReadyToUploadCases(ids));
      for (const id of ids) {
        knownAllocationIdsRef.current.delete(id);
      }
    };
    socket.on("ready_to_upload_removed", handleReadyCaseRemoved);

    return () => {
      socket.emit("leave_ready_to_upload_room");
      socket.off("ready_to_upload_case", handleReadyCase);
      socket.off("ready_to_upload_removed", handleReadyCaseRemoved);
    };
  }, [dispatch, shouldNotifyReadyToUpload, showToast]);

  const closeConfirm = () => {
    if (pendingAction) {
      setSelectedActions((prev) => ({
        ...prev,
        [pendingAction.allocationId]: "",
      }));
    }
    setShowConfirm(false);
    setPendingAction(null);
    setPasskey("");
    setActionNote("");
    setInternalRedesignCategories([]);
    setInternalRedesignSeverity("");
    setInternalAssignmentMode("");
  };

  const toggleInternalRedesignCategory = (category: string, checked: boolean) => {
    setInternalRedesignCategories((prev) =>
      checked ? [...prev, category] : prev.filter((item) => item !== category),
    );
  };

  // CONFIRM HANDLER (API CALL)
  const handleConfirm = () => {
    if (!pendingAction || !canWriteReadyToUpload) return;

    const isInternalRedesign = pendingAction.action === "Internal_redesign";
    const otherSelected = internalRedesignCategories.includes("Other");
    if (isInternalRedesign) {
      if (internalRedesignSeverity !== "Minor" && internalRedesignSeverity !== "Major")
        return;
      if (internalRedesignCategories.length === 0) return;
      if (otherSelected && !actionNote.trim()) return;
      if (internalAssignmentMode !== "old" && internalAssignmentMode !== "new")
        return;
    }

    const isAdmin = user?.role === "Super Admin";
    const finalPasskey = isAdmin ? user?.user_name : passkey;
    const category = isInternalRedesign
      ? [internalRedesignSeverity, ...internalRedesignCategories]
          .filter(Boolean)
          .join(", ")
      : undefined;
    const note = actionNote.trim();

    let payload: any = {
      isAdmin,
      updateCases: [
        {
          allocationId: pendingAction.allocationId,
          action: pendingAction.action,
          action_note: note,
          ...(category ? { category } : {}),
          ...(isInternalRedesign && internalAssignmentMode
            ? { assignment_mode: internalAssignmentMode }
            : {}),
        },
      ],
    };

    // same branching as handleAddNote
    if (isAdmin || finalPasskey) {
      payload.passkey = finalPasskey || "";
    } else {
      if (!employee?.emp_id) return;
      payload.empId = employee.emp_id;
    }
    dispatch(updateAllocatedCases(payload));
  };

  // HANDLE API RESULT
  useEffect(() => {
    if (!pendingAction) return;

    if (allocationState.success) {
      dispatch(removeReadyToUploadCases(pendingAction.allocationId));
      showToast("Case updated successfully", "success");

      setShowConfirm(false);
      setPasskey("");
      setActionNote("");
      setInternalRedesignCategories([]);
      setInternalRedesignSeverity("");
      setInternalAssignmentMode("");
      setPendingAction(null);
    }

    if (allocationState.error) {
      showToast(allocationState.error, "error");
    }

    dispatch(resetAllocationState());
  }, [allocationState.success, allocationState.error]);

  const colCount = 8;
  const isInternalRedesign = pendingAction?.action === "Internal_redesign";
  const otherInternalSelected = internalRedesignCategories.includes("Other");
  const pendingCaseId = readyToUpload.find(
    (item) => item.allocation_id === pendingAction?.allocationId,
  )?.case_id;
  const confirmDisabled =
    !canWriteReadyToUpload ||
    allocationState.loading ||
    !(user?.role === "Super Admin" || employee || passkey.length) ||
    (Boolean(isInternalRedesign) &&
      (internalRedesignCategories.length === 0 ||
        !internalRedesignSeverity ||
        !internalAssignmentMode ||
        (otherInternalSelected && !actionNote.trim())));

  return (
    <>
      <Helmet>
        <title>Ready To Upload</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Ready To Upload"
          subtitle="Review QC-approved cases and route them onward"
        >
          <div className="flex items-center gap-1.5 shrink-0">
            {dateFilters.map((day) => {
              const selected = day.dateKey === selectedDateKey;
              return (
                <button
                  key={day.dateKey}
                  type="button"
                  onClick={() => setSelectedDateKey(day.dateKey)}
                  className={`relative shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold tracking-wide transition ${
                    selected
                      ? "bg-white text-[#2fa9bf]"
                      : "bg-white/15 text-white hover:bg-white/25"
                  }`}
                >
                  {day.dateLabel}
                  {day.hasCases ? (
                    <span
                      className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ${
                        selected ? "ring-white" : "ring-[#2fa9bf]"
                      }`}
                      aria-hidden
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
          <AdminSelect
            value={selectedLab}
            onChange={setSelectedLab}
            className="w-full sm:w-auto"
          >
            <option value={ALL_LABS}>All labs</option>
            {labFilters.map((lab) => (
              <option key={lab.token} value={lab.token}>
                {lab.token} ({lab.count})
              </option>
            ))}
          </AdminSelect>
          <AdminSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by Case ID…"
          />
        </AdminPageHeader>

        <AdminMetaRow
          left={
            <>
              {loading ? (
                <span>Loading cases…</span>
              ) : (
                <span>
                  {filteredCases.length} case
                  {filteredCases.length === 1 ? "" : "s"}
                  {searchTerm.trim() ? " matched" : " ready to upload"}
                  {` on ${formatDateButtonLabel(selectedDateKey)}`}
                  {selectedLab !== ALL_LABS ? ` · ${selectedLab}` : ""}
                </span>
              )}
              {searchTerm.trim() ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="inline-flex items-center gap-1.5 text-[#2fa9bf] hover:text-[#2296aa] font-medium"
                >
                  <FaTimes className="text-[10px]" />
                  Clear search
                </button>
              ) : null}
            </>
          }
        />

        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={`${adminTableHead} text-center`}>SNo</th>
                <th className={adminTableHead}>Case ID</th>
                <th
                  className={`${adminTableHead} break-words whitespace-normal max-w-[20vw]`}
                >
                  Details
                </th>
                <th className={adminTableHead}>QC1</th>
                <th className={adminTableHead}>Designer</th>
                <th className={adminTableHead}>QC2</th>
                <th className={adminTableHead}>Notes</th>
                <th className={`${adminTableHead} text-center`}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading cases…"
                />
              ) : filteredCases.length === 0 ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message={
                    searchTerm.trim()
                      ? "No cases match your search"
                      : `No cases found for ${formatDateButtonLabel(selectedDateKey)}${
                          selectedLab !== ALL_LABS ? ` · ${selectedLab}` : ""
                        }`
                  }
                />
              ) : (
                filteredCases.map((item, index) => (
                  <tr
                    key={item.allocation_id}
                    className={`border-b border-[#F0F0EC] last:border-0 transition-colors ${
                      blinkingCaseIds.has(item.case_id)
                        ? "blink-new-case"
                        : "hover:bg-[#FAFAF8]"
                    }`}
                  >
                    <td className={`${adminTableCell} text-center`}>
                      {index + 1}
                    </td>
                    <td className={adminTableCell}>
                      <div className="flex flex-col items-start gap-1">
                        <a
                          href={caseHrefFromAllocationId(item.case_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#2fa9bf] hover:text-[#2296aa] hover:underline break-all"
                        >
                          {item.case_id}
                        </a>
                        <RedesignScopeTag labels={item.redesign_category} />
                      </div>
                    </td>
                    <td
                      className={`${adminTableCell} break-words whitespace-normal max-w-[20vw]`}
                    >
                      {item.details.split("\n").map((line, i) => (
                        <div
                          key={i}
                          className={
                            line.startsWith("QC2") ? "text-red-500" : ""
                          }
                        >
                          {line}
                        </div>
                      ))}
                    </td>
                    <td className={adminTableCell}>{item.QC1}</td>
                    <td className={adminTableCell}>
                      {item.allocated_Designer}
                    </td>
                    <td className={adminTableCell}>{item.QC2}</td>

                    <td className={adminTableCell}>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-lg bg-[#F0F7F8] text-[#2fa9bf] ring-1 ring-[#D4EBEF] hover:bg-[#E5F4F7] text-xs font-medium transition"
                        onClick={() => {
                          setSelectedAllocationId(item.allocation_id);
                          setOpen(true);
                        }}
                      >
                        <FaStickyNote className="text-[10px]" />
                        View
                      </button>
                    </td>

                    <td className={`${adminTableCell} text-center`}>
                      <AdminSoftSelect
                        value={selectedActions[item.allocation_id] || ""}
                        disabled={showConfirm || !canWriteReadyToUpload}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) return;

                          setSelectedActions((prev) => ({
                            ...prev,
                            [item.allocation_id]: value,
                          }));

                          setPendingAction({
                            allocationId: item.allocation_id,
                            action: value,
                          });
                          setInternalRedesignCategories([]);
                          setInternalRedesignSeverity("");
                          setInternalAssignmentMode("");
                          setActionNote("");
                          setShowConfirm(true);
                        }}
                      >
                        <option value="">Select</option>
                        <option value="Complete">Complete</option>
                        <option value="Internal_redesign">Redesign</option>
                        <option value="Corrupt_Case">Corrupt</option>
                      </AdminSoftSelect>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>

        {/* CONFIRM MODAL */}
        {showConfirm ? (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div
              className={`bg-white rounded-2xl shadow-xl w-full p-6 space-y-4 border border-[#E8E8E4] ${
                isInternalRedesign ? "max-w-lg" : "max-w-sm"
              }`}
            >
              <div>
                <h3 className="text-base font-semibold text-[#4A4A48]">
                  {isInternalRedesign
                    ? "Internal Redesign"
                    : "Confirm action"}
                </h3>
                <p className="text-sm text-[#8A8A85] mt-1.5 leading-relaxed">
                  {isInternalRedesign ? (
                    <>
                      Select Minor or Major, then at least one category to mark{" "}
                      {pendingCaseId ? (
                        <span className="font-medium text-[#2fa9bf]">
                          {pendingCaseId}
                        </span>
                      ) : (
                        "this case"
                      )}{" "}
                      as internal redesign.
                    </>
                  ) : (
                    <>
                      Apply{" "}
                      <span className="font-medium text-[#2fa9bf]">
                        {pendingAction?.action}
                      </span>{" "}
                      to this case?
                    </>
                  )}
                </p>
              </div>

              {isInternalRedesign ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                    Minor or Major
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {INTERNAL_REDESIGN_SEVERITIES.map((option) => {
                      const selected = internalRedesignSeverity === option;
                      return (
                        <label
                          key={option}
                          className={`flex items-center gap-2 border rounded-xl p-3 cursor-pointer ${
                            selected
                              ? "border-[#2fa9bf] bg-[#F0F7F8]"
                              : "border-[#E8E8E4]"
                          }`}
                        >
                          <input
                            type="radio"
                            name="internal-redesign-severity"
                            value={option}
                            checked={selected}
                            onChange={() => setInternalRedesignSeverity(option)}
                            className="text-[#2fa9bf] focus:ring-[#2fa9bf]"
                          />
                          <span className="text-sm font-medium text-[#4A4A48]">
                            {option}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {!internalRedesignSeverity ? (
                    <p className="text-xs text-red-500">
                      Select Minor or Major
                    </p>
                  ) : null}
                </div>
              ) : null}

              {isInternalRedesign ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                    Categories
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 border border-[#E8E8E4] rounded-xl p-3 max-h-52 overflow-y-auto">
                    {INTERNAL_REDESIGN_CATEGORIES.map((category) => {
                      const checked =
                        internalRedesignCategories.includes(category);
                      return (
                        <label
                          key={category}
                          className="flex items-center gap-2 text-sm text-[#4A4A48] cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              toggleInternalRedesignCategory(
                                category,
                                e.target.checked,
                              )
                            }
                            className="rounded text-[#2fa9bf] focus:ring-[#2fa9bf]"
                          />
                          <span>{category}</span>
                        </label>
                      );
                    })}
                  </div>
                  {internalRedesignCategories.length === 0 ? (
                    <p className="text-xs text-red-500">
                      Select at least one category
                    </p>
                  ) : null}
                </div>
              ) : null}

              {isInternalRedesign ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                    Assignment
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      {
                        value: "old" as const,
                        label: "Old",
                        hint: "Same designer and captain, QC2 blank",
                      },
                      {
                        value: "new" as const,
                        label: "New",
                        hint: "Allocate again in the allocation portal",
                      },
                    ]).map((option) => {
                      const selected = internalAssignmentMode === option.value;
                      return (
                        <label
                          key={option.value}
                          className={`flex flex-col gap-0.5 border rounded-xl p-3 cursor-pointer ${
                            selected
                              ? "border-[#2fa9bf] bg-[#F0F7F8]"
                              : "border-[#E8E8E4]"
                          }`}
                        >
                          <span className="flex items-center gap-2 text-sm font-medium text-[#4A4A48]">
                            <input
                              type="radio"
                              name="internal-assignment-mode"
                              value={option.value}
                              checked={selected}
                              onChange={() =>
                                setInternalAssignmentMode(option.value)
                              }
                              className="text-[#2fa9bf] focus:ring-[#2fa9bf]"
                            />
                            {option.label}
                          </span>
                          <span className="text-[11px] text-[#8A8A85] pl-6">
                            {option.hint}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {!internalAssignmentMode ? (
                    <p className="text-xs text-red-500">
                      Select Old or New assignment
                    </p>
                  ) : null}
                </div>
              ) : null}

              {!employee && user?.role !== "Super Admin" ? (
                <AdminSoftInput
                  type="password"
                  placeholder="Enter passkey"
                  value={passkey}
                  onChange={(e) => setPasskey(e.target.value)}
                  className="w-full"
                />
              ) : null}

              {isInternalRedesign ? (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                    Custom note
                    {otherInternalSelected ? " (required)" : ""}
                  </p>
                  <textarea
                    placeholder={
                      otherInternalSelected
                        ? "Enter a custom note (required for Other)"
                        : "Enter a custom note"
                    }
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 resize-none"
                  />
                  {otherInternalSelected && !actionNote.trim() ? (
                    <p className="text-xs text-red-500">
                      A note is required when Other is selected
                    </p>
                  ) : null}
                </div>
              ) : (
                <textarea
                  placeholder="Action note"
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 resize-none"
                />
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeConfirm}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
                >
                  Cancel
                </button>

                <AdminPrimaryButton
                  onClick={handleConfirm}
                  disabled={confirmDisabled}
                >
                  {allocationState.loading ? "Saving…" : "Confirm"}
                </AdminPrimaryButton>
              </div>
            </div>
          </div>
        ) : null}

        {/* CASE NOTES MODAL */}
        <CaseNotesModal
          isOpen={open}
          onClose={() => setOpen(false)}
          allocationId={selectedAllocationId}
        />
      </AdminPageShell>
    </>
  );
};

export default ReadyToUpload;
